import React, { useState, useEffect } from "react";
import { getAllTeamMembers } from "../../services/teamServices";
import { MdPhone, MdMessage, MdEmail } from "react-icons/md";
import Button from "./../../components/Button/Button";

const colorList = [
  "border-l-blue-500",
  "border-l-orange-500",
  "border-l-green-500",
  "border-l-yellow-400",
  "border-l-purple-500",
  "border-l-pink-500",
  "border-l-cyan-500",
  "border-l-red-500",
];

const NosEquipes = () => {
  const [team, setTeam] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Ne convertir en URL absolue que les fichiers côté serveur (/uploads/...)
  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    )
      return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  useEffect(() => {
    const fetchTeamMembers = async () => {
      const data = await getAllTeamMembers();
      setTeam(data);
    };
    fetchTeamMembers();
  }, []);

  const openModal = (imageUrl) => setSelectedImage(imageUrl);
  const closeModal = () => setSelectedImage(null);

  // Regrouper par catégorie
  const groupedTeam = team.reduce((acc, member) => {
    const cat = member.categorie || "Autres";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(member);
    return acc;
  }, {});

  const categories = Object.entries(groupedTeam);

  // Récupère la liste ordonnée des noms de catégories (ex : ["Bureau", "Enseignant", ...])
  const categoryNames = categories.map(([cat]) => cat);

  // Détermine la couleur à utiliser pour la modale du membre sélectionné
  const memberCategoryIndex = categoryNames.indexOf(
    selectedMember?.categorie || "Autres"
  );
  const modalBorderClass = colorList[memberCategoryIndex % colorList.length];

  const handleCall = () => {
    if (selectedMember?.tel_member) {
      window.open(`tel:${selectedMember.tel_member}`, "_blank");
    }
  };

  const handleSms = () => {
    if (selectedMember?.tel_member) {
      window.open(`sms:${selectedMember.tel_member}`, "_blank");
    }
  };

  const handleEmail = () => {
    if (selectedMember?.mail_member) {
      window.open(`mailto:${selectedMember.mail_member}`, "_blank");
    }
  };

  return (
    <section
      className="py-8 bg-lightBackground dark:bg-darkBackground text-black dark:text-white"
      id="nosEquipes"
    >
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-extrabold mb-4 text-primary mx-auto text-center font-satoshi uppercase">
          NOS ÉQUIPES
        </h2>

        {/* Catégories */}
        {categories.map(([categorie, membres], idx) => (
          <div
            key={categorie}
            className={`mb-6 border-l-8 pl-4 ${
              colorList[idx % colorList.length]
            }`}
          >
            <h3 className="text-xl font-bold uppercase mb-2 font-satoshi">
              {categorie}
            </h3>

            <div className="flex flex-wrap gap-4">
              {membres.map((member) => (
                <div
                  key={member.id}
                  className="w-[200px] p-2 bg-white dark:bg-slate-800 rounded shadow-sm text-center transition hover:scale-105 cursor-pointer"
                  onClick={() => setSelectedMember(member)}
                >
                  <img
                    src={toAbsolute(member.photoURL) || "/img/user_avatar.jpg"}
                    alt={`${member.nom} ${member.prenom}`}
                    className="rounded w-32 h-32 object-cover mx-auto mb-1"
                  />
                  <div className="text-sm font-semibold uppercase truncate">
                    {member.nom}
                  </div>
                  <div className="text-sm mb-1 truncate">{member.prenom}</div>
                  <div className="text-xs italic text-primary dark:text-gray-300 font-semibold">
                    {member.fonction}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {selectedMember && (
          <div
            className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm z-50 flex items-center justify-center px-2"
            onClick={() => setSelectedMember(null)}
          >
            <div
              className={`relative bg-white dark:bg-gray-900 text-black dark:text-white rounded-lg w-full max-w-lg shadow-lg p-4 border-l-8 ${
                colorList[
                  categories.findIndex(
                    ([cat]) => cat === selectedMember.categorie
                  ) % colorList.length
                ]
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="absolute top-3 right-3 text-gray-600 dark:text-gray-300 hover:text-red-500 text-xl"
                onClick={() => setSelectedMember(null)}
              >
                &times;
              </button>

              <div className="flex flex-col items-center text-center">
                <img
                  src={
                    toAbsolute(selectedMember.photoURL) ||
                    "/img/user_avatar.jpg"
                  }
                  alt={`${selectedMember.nom} ${selectedMember.prenom}`}
                  className="w-auto min-h-48 max-h-64 rounded-sm object-cover shadow mb-4"
                />
                <h2 className="text-xl">
                  <span className="uppercase font-semibold">
                    {selectedMember.nom}
                  </span>{" "}
                  {selectedMember.prenom}
                </h2>
                <p className="italic text-gray-500 dark:text-gray-300 text-sm font-semibold mb-4">
                  {selectedMember.fonction}
                </p>

                {/* Boutons actions */}
                <div className="flex flex-wrap justify-center gap-3 mb-4">
                  {selectedMember.tel_member && (
                    <>
                      <Button
                        text=""
                        onClick={handleCall}
                        icon={MdPhone}
                        hoverLabel="Appeler"
                      />
                      <Button
                        text=""
                        onClick={handleSms}
                        icon={MdMessage}
                        hoverLabel="Envoyer un SMS"
                      />
                    </>
                  )}
                  {selectedMember.mail_member && (
                    <Button
                      text=""
                      onClick={handleEmail}
                      icon={MdEmail}
                      hoverLabel="Envoyer un email"
                    />
                  )}
                </div>

                <div className="w-full text-left space-y-2 text-sm">
                  {selectedMember.mail_member && (
                    <p>
                      <span className="font-medium">Email :</span>{" "}
                      <a
                        href={`mailto:${selectedMember.mail_member}`}
                        className="text-blue-600 hover:underline"
                      >
                        {selectedMember.mail_member}
                      </a>
                    </p>
                  )}
                  {selectedMember.tel_member && (
                    <p>
                      <span className="font-medium">Téléphone :</span>{" "}
                      {selectedMember.tel_member}
                    </p>
                  )}
                  {selectedMember.bio_member && (
                    <div>
                      <p className="font-medium mb-1">Bio :</p>
                      <div
                        className="prose dark:prose-invert text-sm max-w-none bg-gray-100 dark:bg-gray-800 dark:text-gray-100 p-3 rounded"
                        dangerouslySetInnerHTML={{
                          __html: selectedMember.bio_member,
                        }}
                      />
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setSelectedMember(null)}
                  className="mt-6 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold transition"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modale image */}
        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={closeModal}
          >
            <div className="relative">
              <img
                src={selectedImage}
                alt="Agrandissement"
                className="max-w-full max-h-[80vh] rounded-lg"
              />
              <button
                className="absolute top-2 right-2 text-white text-2xl"
                onClick={closeModal}
                aria-label="Fermer"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default NosEquipes;
